import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, interval, takeUntil } from 'rxjs';

import { ApiService } from '../../core/api.service';

interface TrackingRestaurant {
  id: number;
  name: string;
  slug: string;
  logoUrl?: string | null;
  isOpen?: boolean;
}

interface TrackingProduct {
  id: number;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
}

interface TrackingItem {
  id: number;
  quantity: number;
  unitPrice: number | string;
  total?: number | string;
  notes?: string | null;
  product: TrackingProduct;
}

interface TrackingOrder {
  id: number;
  trackingCode: string;
  status: string;
  subtotal?: number | string;
  deliveryFee?: number | string;
  total: number | string;
  deliveryType: string;
  createdAt: string;
  updatedAt: string;
  restaurant: TrackingRestaurant;
  items: TrackingItem[];
}

@Component({
  selector: 'app-order-tracking',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './order-tracking.component.html',
  styleUrl: './order-tracking.component.css'
})
export class OrderTrackingComponent implements OnInit, OnDestroy {

  order: TrackingOrder | null = null;

  loading = true;
  error = '';

  private trackingCode = '';
 private restaurantSlug = '';
  private readonly destroy$ = new Subject<void>();

  private readonly statusOrder = [
    'PENDING',
    'ACCEPTED',
    'PREPARING',
    'READY',
    'OUT_FOR_DELIVERY',
    'DELIVERED'
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly api: ApiService
  ) {}

  ngOnInit(): void {
    this.trackingCode =
      this.route.snapshot.paramMap.get('trackingCode')?.trim() || '';

      this.restaurantSlug =
  history.state?.restaurantSlug || '';

    if (!this.trackingCode) {
      this.loading = false;
      this.error = 'Código de acompanhamento não informado.';
      return;
    }

    this.loadOrder();

    /*
     * Atualiza o status automaticamente a cada 15 segundos.
     */
    interval(15000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isFinished()) {
          this.loadOrder(false);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadOrder(showLoading = true): void {
    if (showLoading) {
      this.loading = true;
    }

    this.error = '';

    this.api.get<TrackingOrder>(
      `/orders/tracking/${encodeURIComponent(this.trackingCode)}`
    ).subscribe({
      next: response => {
        this.order = response;
        this.loading = false;

        this.updateStoredOrder();
      },

      error: error => {
        console.error(
          'Erro ao consultar acompanhamento:',
          error
        );

        this.loading = false;

        this.error =
          error?.error?.message ||
          'Não foi possível acompanhar o pedido.';
      }
    });
  }

  getStatusLabel(status?: string): string {
    const labels: Record<string, string> = {
      PENDING: 'Pedido recebido',
      ACCEPTED: 'Pedido confirmado',
      PREPARING: 'Pedido em preparo',
      READY: 'Pedido pronto',
      OUT_FOR_DELIVERY: 'Saiu para entrega',
      DELIVERED: 'Pedido entregue',
      CANCELED: 'Pedido cancelado'
    };

    return labels[status || ''] || 'Status desconhecido';
  }

  getStatusDescription(status?: string): string {
    const descriptions: Record<string, string> = {
      PENDING:
        'O restaurante recebeu seu pedido e fará a confirmação em breve.',

      ACCEPTED:
        'O restaurante confirmou o seu pedido.',

      PREPARING:
        'Seu pedido está sendo preparado.',

      READY:
        this.order?.deliveryType === 'PICKUP'
          ? 'Seu pedido está pronto para retirada.'
          : 'Seu pedido está pronto e aguardando a próxima etapa.',

      OUT_FOR_DELIVERY:
        'Seu pedido saiu para entrega.',

      DELIVERED:
        'Seu pedido foi finalizado com sucesso.',

      CANCELED:
        'O pedido foi cancelado.'
    };

    return descriptions[status || ''] || '';
  }

  isStatusCompleted(status: string): boolean {
    if (!this.order || this.order.status === 'CANCELED') {
      return false;
    }

    const currentIndex = this.statusOrder.indexOf(this.order.status);
    const statusIndex = this.statusOrder.indexOf(status);

    return statusIndex <= currentIndex;
  }

  isCurrentStatus(status: string): boolean {
    return this.order?.status === status;
  }

  shouldShowDeliveryStatus(): boolean {
    return this.order?.deliveryType === 'DELIVERY';
  }

  shouldShowReadyStatus(): boolean {
    return true;
  }

  isFinished(): boolean {
    return (
      this.order?.status === 'DELIVERED' ||
      this.order?.status === 'CANCELED'
    );
  }

  getDeliveryTypeLabel(): string {
    const labels: Record<string, string> = {
      DELIVERY: 'Entrega',
      PICKUP: 'Retirada',
      TABLE: 'Pedido na mesa'
    };

    return labels[this.order?.deliveryType || ''] || '';
  }

  formatMoney(value: number | string | undefined): string {
    const amount = Number(value || 0);

    return amount.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  formatDate(value?: string): string {
    if (!value) {
      return '';
    }

    return new Date(value).toLocaleString('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short'
    });
  }

  getImageUrl(imageUrl?: string | null): string {
    if (!imageUrl) {
      return '';
    }

    if (
      imageUrl.startsWith('http://') ||
      imageUrl.startsWith('https://')
    ) {
      return imageUrl;
    }

    return `http://localhost:3000${imageUrl}`;
  }

  // goToMenu(): void {
  //   const slug = this.order?.restaurant?.slug;

  //   if (slug) {
  //     this.router.navigate(['/cardapio', slug]);
  //     return;
  //   }

  //   this.router.navigate(['/']);
  // }


  goToMenu(): void {
  const slug =
    this.order?.restaurant?.slug?.trim() ||
    this.restaurantSlug?.trim();

  if (!slug) {
    console.error(
      'Slug do restaurante não encontrado:',
      this.order?.restaurant
    );

    alert(
      'Não foi possível identificar o cardápio do restaurante.'
    );

    return;
  }

  this.router.navigate([
    '/cardapio',
    slug
  ]);
}

  private updateStoredOrder(): void {
    if (!this.order?.restaurant?.id) {
      return;
    }

    const key =
      `activeOrder_${this.order.restaurant.id}`;

    if (
      this.order.status === 'DELIVERED' ||
      this.order.status === 'CANCELED'
    ) {
      localStorage.removeItem(key);
      return;
    }

    localStorage.setItem(
      key,
      JSON.stringify({
        trackingCode: this.order.trackingCode,
        restaurantId: this.order.restaurant.id,
        status: this.order.status
      })
    );
  }
}